"use client";

import { useState, useEffect } from "react";
import { AlignJustify } from "@/components/icons";
import { ShareButton, InfoCallout } from "@/components/ui/";
import { TOC_ITEMS } from "./data";

export { InfoCallout };

export function StepList({ steps }: { steps: { num: number; label: string; desc: string }[] }) {
  return (
    <ol className="my-6 list-none space-y-4 pl-0">
      {steps.map((step) => (
        <li key={step.num} className="flex items-start gap-4">
          <span
            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold"
            style={{ background: "rgba(0,229,179,0.2)", color: "var(--brand-green)" }}
          >
            {step.num}
          </span>
          <span className="text-muted-foreground pt-0.5 text-sm leading-relaxed">
            <strong className="text-foreground font-semibold">{step.label}:</strong> {step.desc}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function ComparisonTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div
      className="my-8 overflow-hidden rounded-2xl border"
      style={{ borderColor: "color-mix(in srgb, var(--foreground) 5%, transparent)" }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: "color-mix(in srgb, var(--foreground) 4%, transparent)" }}>
              {headers.map((h) => (
                <th
                  key={h}
                  className="text-muted-foreground px-5 py-3 text-left text-xs font-semibold tracking-wider uppercase"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row[0]}
                className="border-t"
                style={{
                  borderColor: "color-mix(in srgb, var(--foreground) 4%, transparent)",
                  background:
                    i % 2 === 0
                      ? "transparent"
                      : "color-mix(in srgb, var(--foreground) 1%, transparent)",
                }}
              >
                {row.map((cell, j) => (
                  <td
                    key={cell}
                    className={`px-5 py-3 ${j === 0 ? "text-foreground font-semibold" : "text-muted-foreground"}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { ShareButton };

export function TableOfContents() {
  const [activeId, setActiveId] = useState<string>(TOC_ITEMS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    TOC_ITEMS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) setActiveId(id);
        },
        { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <aside
      className="sticky top-24 hidden w-52 shrink-0 self-start xl:block"
      aria-label="Table of contents"
    >
      <div className="mb-5 flex items-center gap-2.5">
        <AlignJustify size={15} className="text-muted-foreground shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground text-sm font-medium">On this page</span>
      </div>

      <nav>
        <ol className="flex flex-col">
          {TOC_ITEMS.map((item) => {
            const isActive = activeId === item.id;
            return (
              <li key={item.id} className="relative">
                <span
                  className="absolute top-0 bottom-0 left-0 w-px"
                  style={{ background: isActive ? "var(--foreground)" : "var(--border)" }}
                  aria-hidden="true"
                />
                <a
                  href={`#${item.id}`}
                  onClick={() => setActiveId(item.id)}
                  className="block py-2 pl-4 text-sm leading-snug transition-colors duration-150"
                  style={{
                    color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                    fontWeight: isActive ? 600 : 400,
                  }}
                >
                  {item.label}
                </a>
              </li>
            );
          })}
        </ol>
      </nav>
    </aside>
  );
}
